import { useMemo } from 'react';

import { useAuth } from '@/shared/contexts/AuthContext';

import logger from '../utils/logger';

import {
    useAuthenticatedAxios,
    createAuthenticatedAxios,
} from './axiosInstance';
import publicAxios from './publicAxios';

// hook for API operations
export const useUserApi = () => {
    const api = useAuthenticatedAxios();
    const { user } = useAuth();

    return useMemo(
        () => ({
            createProfile: async (profileData) => {
                const signupRefreshApi = createAuthenticatedAxios(user);
                const response = await signupRefreshApi.post(
                    '/api/users',
                    profileData
                );
                return response.data;
            },

            fetchProfile: async () => {
                const response = await api.get('/api/users/me');
                return response.data;
            },

            updateProfile: async (profileData) => {
                const response = await api.put('/api/users/me', profileData);
                return response.data;
            },

            // upload profile picture for the authenticated user
            uploadProfilePicture: async (formData) => {
                try {
                    const response = await api.post(
                        '/api/users/me/profile-picture',
                        formData,
                        {
                            headers: {
                                'Content-Type': 'multipart/form-data',
                            },
                            // Track upload progress
                            onUploadProgress: (progressEvent) => {
                                const percentCompleted = Math.round(
                                    (progressEvent.loaded * 100) /
                                        progressEvent.total
                                );
                                logger.debug(
                                    `Upload progress: ${percentCompleted}%`
                                );
                            },
                        }
                    );

                    return response.data;
                } catch (error) {
                    logger.error('Error uploading profile picture:', error);
                    throw error;
                }
            },

            // Mark onboarding tour as complete
            markTourComplete: async () => {
                const response = await api.put('/api/users/me/tour-status', {
                    complete: true,
                });
                return response.data;
            },

            // Save current tour step for resumption
            saveTourStep: async (tourStep) => {
                const response = await api.put('/api/users/me/tour-status', {
                    tourStep,
                });
                return response.data;
            },

            // Reset tour to beginning (hasSeenTour=false, tourStep=0)
            resetTour: async () => {
                const response = await api.put('/api/users/me/tour-status', {
                    reset: true,
                });
                return response.data;
            },

            // set a real username for users with an auto-generated one
            setupUsername: async (username) => {
                const response = await api.put('/api/users/me/username', {
                    username,
                });
                return response.data;
            },

            // delete profile picture for the authenticated user
            deleteProfilePicture: async () => {
                try {
                    const response = await api.delete(
                        '/api/users/me/profile-picture'
                    );
                    return response.data;
                } catch (error) {
                    logger.error('Error deleting profile picture:', error);
                    throw error;
                }
            },
        }),
        [api, user]
    );
};

export default useUserApi;

/**
 * Fetch a public user profile by username (no auth required).
 * @param {string} username
 * @returns {Promise<object>} Public profile data
 */
export async function fetchPublicProfile(username) {
    const response = await publicAxios.get(
        `/api/users/${encodeURIComponent(username)}`
    );
    return response.data;
}

/**
 * Fetch public posts for a user (no auth required).
 * @param {string} username
 * @param {number} [page=1]
 * @param {number} [limit=20]
 * @returns {Promise<object>} { posts, pagination }
 */
export async function fetchUserPosts(username, page = 1, limit = 20) {
    const response = await publicAxios.get(
        `/api/users/${encodeURIComponent(username)}/posts?page=${page}&limit=${limit}`
    );
    return response.data;
}
